import React, { useState, useCallback } from 'react';
import { Card, Button, Flex, Typography, theme, Modal, Dropdown, notification } from 'antd';
import { EditOutlined, DeleteOutlined, CopyOutlined, MoreOutlined } from '@ant-design/icons';
import { FormIntegrationFunction } from '../FormIntegrationFunction/formIntegrationFunction';
import { HttpRequest } from '../../../../../../utils/httpClient';

import { createLogger } from '../../../../../../utils/logger';
const logger = createLogger('features-assistants');

const { useToken } = theme;
const { Text } = Typography;

interface CardIntegrationActionProps {
  message: string;
  onDelete: () => void;
  onDuplicate: () => void;
  onEdit: (newMessage: string) => void;
  integrationData: {
    logo: string;
    name: string;
    id: string;
  };
  availableActions?: any[];
  onActionSelect?: (actionId: string) => void;
  selectedActionId?: string;
  selectedAction?: any;
  chatbotId?: string;
  flowId?: string;
}

export const CardIntegrationAction: React.FC<CardIntegrationActionProps> = ({
  message,
  onDelete,
  onDuplicate,
  onEdit,
  integrationData,
  availableActions = [],
  onActionSelect,
  selectedActionId,
  selectedAction,
  chatbotId,
  flowId
}) => {
  const { token } = useToken();
  const [showEditModal, setShowEditModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Extraer el ID de la función del mensaje
  const getFunctionId = (msg: string) => {
    const match = msg.match(/@[^/]+\/([^{]+)/);
    return match ? match[1] : '';
  };
  
  // Extraer el nombre de la función del mensaje
  const getFunctionName = (msg: string) => {
    try {
      const match = msg.match(/\{.*\}/);
      if (match) {
        const functionInfo = JSON.parse(match[0]);
        if (functionInfo.name) {
          return functionInfo.name;
        }
      }
    } catch (error) {
      logger.error('Error parsing function name:', error);
    }

    // Si no se puede extraer el nombre, usar el ID formateado como fallback
    const functionId = getFunctionId(msg);
    return functionId
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  // Extraer los parámetros del mensaje
  const extractParams = (msg: string) => {
    try {
      const match = msg.match(/\{.*\}/);
      if (match) {
        const functionInfo = JSON.parse(match[0]);
        return functionInfo.parameters || {};
      }
      return {};
    } catch (error) {
      logger.error('Error parsing message parameters:', error);
      return {};
    }
  };
  
  // Extraer el ID de la acción del mensaje
  const getActionId = (msg: string) => {
    try {
      const match = msg.match(/\{.*\}/);
      if (match) {
        const functionInfo = JSON.parse(match[0]);
        return functionInfo.actionId;
      }
    } catch (error) {
      logger.error('Error parsing action ID:', error);
    }
    return null;
  };
  
  const functionId = getFunctionId(message);
  const functionName = getFunctionName(message);
  const actionId = getActionId(message);
  const params = extractParams(message);
  
  // Preparar los datos de la función para el formulario
  const prepareFunctionData = useCallback(() => {
    return {
      id: functionId,
      title: functionName,
      name: functionName,
      description: `Function for ${integrationData.name}`,
      parameters: {
        properties: Object.entries(params).reduce((acc: any, [key, value]) => {
          acc[key] = {
            description: key,
            type: typeof value === 'number' ? 'number' : 'string',
            form: true,
            value: value
          };
          return acc;
        }, {}),
        required: []
      },
      integration: {
        name: integrationData.name,
        id: integrationData.id,
        logo: integrationData.logo
      },
      actionId: actionId
    };
  }, [functionId, functionName, integrationData, params, actionId]);
  
  const handleEditClick = () => {
    setShowEditModal(true);
  };
  
  const handleFormSave = (newMessage: string) => {
    onEdit(newMessage);
    setShowEditModal(false);
  };
  
  const handleFormCancel = () => {
    setShowEditModal(false);
  };
  
  // Función para eliminar la acción de la base de datos
  const deleteAction = async (actionId: string) => {
    try {
      const actionRequest = new HttpRequest(`${process.env.REACT_APP_API_HOST}/flows/actions`);
      const response = await actionRequest.delete(`${actionId}`);
      return response.status === 200;
    } catch (error) {
      logger.error('Error deleting action:', error);
      return false;
    }
  };

  const handleDeleteClick = async () => {
    const actionId = getActionId(message);
    if (actionId) {
      setIsDeleting(true);
      try {
        const success = await deleteAction(actionId);
        if (success) {
          notification.success({
            message: 'Success',
            description: 'Action deleted successfully'
          });
          onDelete();
        } else {
          notification.error({
            message: 'Error',
            description: 'Failed to delete action'
          });
        }
      } catch (error: any) {
        logger.error('Error in delete process:', error);
        notification.error({
          message: 'Error',
          description: error?.message || 'Failed to delete action'
        });
      } finally {
        setIsDeleting(false);
      }
    } else {
      // Si no hay actionId, solo eliminar el mensaje
      onDelete();
    }
  };
  
  const menuItems = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: handleEditClick
    },
    {
      key: 'duplicate',
      label: 'Duplicate',
      icon: <CopyOutlined />,
      onClick: onDuplicate
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      onClick: handleDeleteClick,
      disabled: isDeleting
    }
  ];
  
  return (
    <>
      <Card 
        style={{ 
          maxWidth: '400px',
          width: '100%',
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowTertiary,
          cursor: 'pointer'
        }}
        bodyStyle={{ padding: token.paddingSM }}
        onClick={handleEditClick}
      >
        <Flex align="center" justify="space-between" style={{ width: '100%' }}>
          <Flex gap={token.marginMD} align="center">
            <img 
              src={integrationData.logo} 
              alt={`${integrationData.name} Logo`} 
              style={{ 
                maxHeight: 70,
                width: 120,
                objectFit: 'contain' 
              }} 
            />
            <Flex vertical gap={token.marginXS}>
              <Text strong style={{ fontSize: '16px', lineHeight: '24px', margin: 0 }}>
                {integrationData.name}
              </Text>
              <Text type="secondary" style={{ fontSize: '14px', lineHeight: '20px', margin: 0 }}>
                {functionName}
              </Text>
            </Flex>
          </Flex>
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button 
                icon={<MoreOutlined />} 
                size="small"
                type="text"
                loading={isDeleting}
              />
            </Dropdown>
          </div>
        </Flex>
      </Card>
      
      <Modal
        title={`Edit ${integrationData.name} Function`}
        open={showEditModal}
        onCancel={handleFormCancel}
        footer={null}
        width={700}
        destroyOnClose={true}
      >
        <FormIntegrationFunction
          functionData={prepareFunctionData()}
          onBack={handleFormCancel}
          onSave={handleFormSave}
          chatbotId={chatbotId}
          flowId={flowId}
        />
      </Modal>
    </>
  );
}; 